/**
 * Exchange variant key (variants block plan 1) — the CHAT/CARD/TEXT/THREAD rail
 * that generalizes the retired SANS_CHAT_FLOW boolean. Names are user-ratified
 * and permanent (they enter the analytics stream): CHAT = the ungated landing,
 * CARD = the shipped sans-chat build, TEXT = the post-party direction (plan 2),
 * THREAD = the gated chat (plan 3).
 * Design contract: docs/blocks/tiered-exchange-epic.md § Exchange variants;
 * mechanics: docs/core/exchange-variants.md.
 *
 * Variants are PRESENTATION-ONLY by rule (litmus: two testers on different
 * variants can still exchange with each other cleanly) — formation writes are
 * unconditional; only render/nav/copy/payload-shape gates read this key.
 *
 * Resolution — ONCE per page load (memoized; the dev switcher reloads):
 *   main lock → URL param `?v=` → localStorage `exchange_variant` → the default.
 * The URL param is per-load and NEVER persisted (scan-carries-variant: a
 * scanned QR tag mirrors the HOST for that visit only — no stale carryover on
 * the anon device). The store is written ONLY by the dev switcher (the host
 * device's own choice). On main builds the key is LOCKED to the default, reads
 * AND writes — prod testers can't wander into unvalidated flows.
 *
 * MAIN_DEFAULT_VARIANT is that default, and it is the ONLY thing a default flip
 * changes: it governs the main lock, the no-input fallthrough, and which
 * variant's QR tag is suppressed. Plan 3 Step 17 moves it to THREAD.
 */

import { isDevToolsEnabled } from './devTools';

export const VARIANT_CHAT = 'chat';
export const VARIANT_CARD = 'card';
export const VARIANT_TEXT = 'text';
export const VARIANT_THREAD = 'thread';
export const VARIANTS = [VARIANT_CHAT, VARIANT_CARD, VARIANT_TEXT, VARIANT_THREAD];

// The variant a main build is locked to. Flipped chat → thread at plan 3
// Step 17 (D1/D1a) — the whole default change is this one constant, so the
// undo is reverting one line rather than a branch of code.
//
// This is production-facing beyond the screen swap: D1a notes it also turns
// Simple-profile minting ON for real testers, because the downstream ladder
// keys off the variant. That is the Exchange Validation milestone's point
// (step-3 propagation has been dark on flag-off main), but it is the wedge
// going live on real people — so whether this commit MERGES is called at
// /go:review on device evidence, not here.
export const MAIN_DEFAULT_VARIANT = VARIANT_THREAD;

export const VARIANT_STORAGE_KEY = 'exchange_variant';

// ?v= accepts letter or word spellings (refine Pass 2: the ONE param).
// 'h' is THREAD's letter — 't' was already taken by TEXT.
const PARAM_VALUES = {
  c: VARIANT_CARD,
  card: VARIANT_CARD,
  t: VARIANT_TEXT,
  text: VARIANT_TEXT,
  h: VARIANT_THREAD,
  thread: VARIANT_THREAD,
  chat: VARIANT_CHAT,
};

// The QR tag a host appends to their permanent URL (scan-carries-variant).
// Keyed for EVERY variant; getVariantQrTag() then suppresses whichever one is
// currently the default. Chat has no single letter, so it spells the word —
// only ever needed on a branch deploy after the default moves off chat, where
// the 3 extra bytes are well inside the budget.
const QR_TAGS = {
  [VARIANT_CHAT]: '?v=chat',
  [VARIANT_CARD]: '?v=c',
  [VARIANT_TEXT]: '?v=t',
  [VARIANT_THREAD]: '?v=h',
};

// Deliberately reads the env var, not deployInfo's SOURCE_BRANCH (Existing
// Rails First — the second copy has a named reason): SOURCE_BRANCH is captured
// at import, and this module's tests flip REACT_APP_BRANCH at runtime to prove
// the main lock. Same derivation, per-call read.
const isMainBuild = () => (process.env.REACT_APP_BRANCH || 'local') === 'main';

// Snapshot the arrival URL's search at module eval, BEFORE any runtime code
// can clean it (App.js and PWAAuthScreen replaceState away query params) —
// "resolve once per page load" is structural, not an accident of call order.
const captureSearch = () => {
  try {
    return window.location.search;
  } catch {
    return '';
  }
};

let initialSearch = captureSearch();

const resolveVariant = () => {
  // Main serves the default to EVERYONE — except a browser that has turned dev
  // tools on for itself (`?dev=1`, utils/devTools.js).
  //
  // Fixed 2026-08-20, the missing half of that day's work: the switcher pill was
  // moved off the branch gate onto isDevToolsEnabled() so it would RENDER on
  // main, but this line was left returning before the store was ever read. The
  // pill cycled, wrote the store, reloaded — and main ignored it. Half the job.
  //
  // The lock this line exists for is unchanged: a real tester has no dev flag,
  // so they cannot be moved off the default by a stored value or a stray ?v= in
  // a link. It is now gated on the person, matching the pill it serves.
  if (isMainBuild() && !isDevToolsEnabled()) return MAIN_DEFAULT_VARIANT;

  // URL param — this load only, never written to the store by arrival.
  try {
    const param = new URLSearchParams(initialSearch).get('v');
    const fromParam = param && PARAM_VALUES[param.toLowerCase()];
    if (fromParam) return fromParam;
  } catch {
    /* malformed search — fall through to the store */
  }

  try {
    const stored = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (VARIANTS.includes(stored)) return stored;
  } catch {
    /* storage unavailable — default */
  }

  return MAIN_DEFAULT_VARIANT;
};

let resolvedVariant = null;

/**
 * The active variant for this page load: 'chat' | 'card' | 'text' | 'thread'.
 * Invalid or missing inputs resolve to the default, never a crash.
 */
export const getExchangeVariant = () => {
  if (resolvedVariant === null) resolvedVariant = resolveVariant();
  return resolvedVariant;
};

/**
 * The boolean-equivalent the SANS_CHAT_FLOW census reads (22 of 24 gates):
 * card and text both ride the sans-chat rails; chat is main's flow.
 */
export const isSansChatVariant = () => {
  const variant = getExchangeVariant();
  return variant === VARIANT_CARD || variant === VARIANT_TEXT;
};

/**
 * TEXT's own seat at the seams (plan 2): the AnonExchangeFlow 3-way and the
 * host-side People choreography read this; everything variant-shared keeps
 * reading isSansChatVariant(). Read-only — store writers stay exactly two.
 */
export const isTextVariant = () => getExchangeVariant() === VARIANT_TEXT;

/**
 * THREAD's seat at the seams (plan 3): the gated thread landing, the hard
 * composer lock, and the People treatment read this. Read-only — store
 * writers stay exactly two.
 */
export const isThreadVariant = () => getExchangeVariant() === VARIANT_THREAD;

/**
 * The downstream ladder (plan 3 D3): the Simple-profile mint door, "Make
 * custom QR", and the People-as-value-surface treatment that goes with them.
 * CARD and TEXT have always had it; THREAD gets it because without the ladder
 * it produces no step-2/step-3 data, and step-3 frequency is the Exchange
 * Validation milestone's hero metric.
 *
 * This is the ONLY thing THREAD takes from the sans-chat side — it is NOT
 * sans-chat (it renders a live thread), so `isSansChatVariant()` stays
 * card‖text and the other 19 census gates inherit CHAT untouched. Named here
 * rather than hand-composed at the three call sites: composing the compound
 * inline is the re-derivation drift `userTier.js` was written to retire.
 */
export const hasDownstreamLadder = () => isSansChatVariant() || isThreadVariant();

/**
 * QR-URL suffix for the host's permanent URL ('' | '?v=c' | '?v=t' | '?v=h').
 *
 * The tag exists to carry a NON-DEFAULT variant, so whichever variant is
 * currently main's default appends nothing — that is what keeps the
 * production QR byte count untouched across a default flip (plan 3 D1a).
 * Hard-coding the empty case to 'chat' would silently start appending 4 bytes
 * to every production QR the moment MAIN_DEFAULT_VARIANT moves to THREAD.
 *
 * Byte budget: ≤ 7 chars; variant-testing branch names stay ≤ 17 chars so the
 * tagged branch-deploy URL fits the 58-byte QR ceiling (CLAUDE-PROJECT § QR).
 */
export const getVariantQrTag = () => {
  const variant = getExchangeVariant();
  if (variant === MAIN_DEFAULT_VARIANT) return '';
  return QR_TAGS[variant] || '';
};

/**
 * The dev switcher's ONE write door to the store. No-ops on invalid values,
 * and on main for anyone WITHOUT dev tools on — same person-gate as the read
 * path, for the same reason (fixed 2026-08-20: the read was unblocked on main
 * and this was left blocked, so the pill still saved nothing).
 *
 * Both halves must move together. A read that honours the store while the
 * write is blocked is a switcher that cannot switch; a write that lands while
 * the read is blocked is a stored value nothing obeys.
 *
 * The switcher reloads after writing — resolve-once means no mid-flow flips.
 */
export const setStoredVariant = (variant) => {
  if (!VARIANTS.includes(variant)) return;
  if (isMainBuild() && !isDevToolsEnabled()) return;
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch {
    /* storage unavailable — the switcher just won't stick */
  }
};

/**
 * The SP-mint write door (review 2026-08-13 — the tier-crack fix). Minting a
 * Simple profile is the device choosing a side: the person committed to an
 * identity that only exists under the CARD/TEXT rails, so the variant that
 * hosted the mint persists to the store — otherwise their next bare open
 * (home-screen icon, stripped URL) resolves 'chat', isSimpleTier() flips
 * false, and every SP surface silently vanishes while getUserTier() still
 * says simple. Store writers are exactly two: the dev switcher and this.
 */
export const persistVariantAtMint = () => {
  setStoredVariant(getExchangeVariant());
};

/** Test-only: clear the per-load memo and re-snapshot the URL. */
export const __resetExchangeVariantForTests = () => {
  resolvedVariant = null;
  initialSearch = captureSearch();
};
